/**
 * derives — THE NAMED-DERIVE SET (README §7.3).
 *
 * Two families:
 *   ROW ANNOTATIONS   `(rows, args) → rows'`, each row gaining the
 *                     presentation-ready fields a generic card binds by name
 *                     (`status_tone`, `calorie_label`, `cover`) — the card
 *                     renders them blindly and knows nothing about expiry,
 *                     calories or toxicity.
 *   PROJECTIONS       `(rows, args) → a new shape` (counts, groups, streams).
 *
 * Every function is PURE: `(rows, args, feed) → value`, recomputed on every
 * feed change, no memory between feeds. The iOS runtime implements the same
 * names with the same outputs — that identity is why the names matter.
 *
 * This is the ONE layer allowed to carry domain vocabulary. A component
 * contract may not say "expiry" or "calories"; a derive may, because it is
 * data. The NAMES, though, stay generic wherever the shape is generic
 * (`latestChildStatus`, `rankStatus`, `facetCounts`) — a derive named after a
 * widget can only ever serve that widget.
 *
 * GRACEFUL, NOT CLOSED (README §7.6 + §4.5). The set used to be closed and
 * fail-closed: an unknown name rendered a red placeholder. That is the wrong
 * failure for a hot-updatable blueprint running against an app build that
 * shipped earlier — the derive-level analogue of a component fallback is what
 * is wanted, so `runDerive` returns an EMPTY result and REPORTS the name. The
 * page then degrades through `when` / EmptyState (a section over no rows does
 * not draw) instead of breaking. A derive that THROWS degrades the same way.
 */
import { formatDate, formatNumber, getPath, matchesWhere, num } from './paths';
import { buildGroup } from './fold';

// ── date helpers (local time — a food log is a human day, not a UTC day) ────
const DAY_MS = 86400000;

export function isoDay(d) {
  const dt = d instanceof Date ? d : new Date(d);
  if (Number.isNaN(dt.getTime())) return '';
  const y = dt.getFullYear();
  const m = String(dt.getMonth() + 1).padStart(2, '0');
  const day = String(dt.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export function todayIso() {
  return isoDay(new Date());
}

/**
 * THE DAY A VALUE BELONGS TO, for anything a component may be handed.
 *
 * `isoDay` is the local-day rule the whole derive layer uses; the calendar was
 * bucketing on `String(value).slice(0,10)`, which is the UTC day for a
 * timestamp — so east of UTC an evening capture landed on TOMORROW's cell and
 * west of it a small-hours one landed on YESTERDAY's, one cell off from every
 * date-bucketed list on the same page.
 *
 * The short-circuit is the other half, and it is not an optimisation: a bare
 * `"2026-08-05"` parses as UTC MIDNIGHT, so putting it through `isoDay` in a
 * negative-offset zone would move it back a day — the very bug, inverted. A day
 * string is already a day; only a timestamp needs localising.
 */
export function localDay(v) {
  if (v == null || v === '') return '';
  const s = String(v);
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  return isoDay(v);
}

/**
 * A usable YYYY-MM-DD. Every date-shaped arg goes through this before it can
 * anchor a window: a blueprint arg may legitimately be absent (`page_args.date`
 * on first paint), and an absent anchor must mean TODAY, never `Invalid Date`.
 */
function isDayString(v) {
  return typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v.slice(0, 10)) && !Number.isNaN(Date.parse(v.slice(0, 10)));
}

/** The day a row belongs to: explicit `payload.date`, else its write day. */
function dayOf(row) {
  if (typeof row?.date === 'string' && /^\d{4}-\d{2}-\d{2}/.test(row.date)) return row.date.slice(0, 10);
  return row?.created_at ? isoDay(row.created_at) : '';
}

/**
 * LOCKED: dates are ALWAYS en-US, whatever the device locale is set to.
 * `toLocaleDateString(undefined, …)` reads the RUNTIME's locale, so a zh-CN
 * phone rendered "7月28日周二" inside an en-US widget — and today's label reads
 * "Today", which hides it until the user pages back a day. If this ever becomes
 * configurable it must still DEFAULT to en-US, not to the device.
 */
const DATE_LOCALE = 'en-US';

function dayLabel(iso) {
  const t = todayIso();
  if (iso === t) return 'Today';
  const y = isoDay(new Date(Date.now() - DAY_MS));
  if (iso === y) return 'Yesterday';
  const dt = new Date(`${iso}T12:00:00`);
  if (Number.isNaN(dt.getTime())) return iso;
  return dt.toLocaleDateString(DATE_LOCALE, { weekday: 'short', month: 'short', day: 'numeric' });
}

/**
 * THE `as` GUARD — write a derive's OUTPUT fields onto a row, refusing to eat
 * a field the row already carries.
 *
 * A row annotation names its outputs (`as: "care"` → `care`, `care_tone`,
 * `care_label`, …) and a feed row is MODEL-AUTHORED, so nothing stops the
 * payload from already having a `care`. The old spread wrote straight over it:
 * the row still looked annotated, the card still bound `care_tone`, and the
 * value the agent actually logged was gone with no trace anywhere — the worst
 * shape a bug can take, because every visible symptom says "working".
 *
 * So a collision KEEPS THE PAYLOAD and REPORTS itself through the same
 * fail-closed channel an unknown derive uses: the studio panel lists it, the
 * author renames the `as`, and no page silently shows a derived value where a
 * logged one belongs (README §7.3 — a derive's `as` must not name a payload
 * field). An IDENTICAL value is not a collision: re-running the same
 * annotation over its own output is a no-op, not an error.
 */
function annotate(row, fields, report) {
  const out = { ...row };
  for (const [k, v] of Object.entries(fields)) {
    const prev = row?.[k];
    if (present(prev) && prev !== v) { report?.(k); continue; }
    out[k] = v;
  }
  return out;
}

// ── 1. statusCounts(meds, { soon_days }) ───────────────────────────────────
// Expiry triage for medicine-box. Status semantics are LOCKED (expired = bad,
// soon = warn, no date = muted); the palette is the kit's business.

/** Per-row expiry status — `statusCounts` and `expiryStatus` share it. */
function expiryOf(row, soonDays = 30) {
  const raw = row?.expiry || row?.expiry_date || row?.expires_at || null;
  if (!raw) return 'nodate';
  const d = new Date(`${String(raw).slice(0, 10)}T00:00:00`);
  if (Number.isNaN(d.getTime())) return 'nodate';
  const days = Math.round((d - new Date(`${todayIso()}T00:00:00`)) / DAY_MS);
  if (days < 0) return 'expired';
  if (days <= soonDays) return 'soon';
  return 'ok';
}

function statusCounts(rows, args = {}) {
  const soonDays = num(args.soon_days) || 30;
  const counts = { expired: 0, soon: 0, nodate: 0, ok: 0, total: rows.length };
  for (const r of rows) counts[expiryOf(r, soonDays)] += 1;
  const bits = [`${counts.total} item${counts.total === 1 ? '' : 's'}`];
  if (counts.expired) bits.push(`${counts.expired} expired`);
  else if (counts.soon) bits.push(`${counts.soon} expiring soon`);
  counts.summary = bits.join(' · ');
  return counts;
}

// ── 2. todayTotals(meals, { date, goal }) ──────────────────────────────────
// HONEST energy: the day total plus the low–high band the estimates carry. A
// macro with no data stays null — unknown is never zero.
//
// It emits PREFORMATTED `*_label` strings beside the raw numbers, the same way
// it already emits `day_label` beside `date`. A blueprint interpolates copy
// ("{totals.day_label} · {totals.calories} cal logged"), and interpolation is
// deliberately LOSSLESS — it must never reformat, or an id, a path or a session
// key passing through it would grow thousands separators. So a number a
// blueprint puts in its own copy has to arrive already formatted, or the page
// title reads "1770" above a chart reading "1,770".
function todayTotals(rows, args = {}) {
  const date = isDayString(args.date) ? args.date.slice(0, 10) : todayIso();
  const goal = args.goal || {};
  const dayRows = rows.filter((r) => dayOf(r) === date);
  const sum = (field) => dayRows.reduce((s, r) => s + num(r[field]), 0);
  const anyOf = (field) => dayRows.some((r) => r[field] != null && r[field] !== '');

  const calories = sum('calories');
  const low = dayRows.reduce((s, r) => s + num(r.calories_low ?? r.calories), 0);
  const high = dayRows.reduce((s, r) => s + num(r.calories_high ?? r.calories), 0);
  const target = goal.calorie_target != null ? num(goal.calorie_target) : null;

  const macro = (key, label, targetKey, unit) => ({
    key,
    label,
    value: anyOf(key) ? Math.round(sum(key)) : null,
    target: goal[targetKey] != null ? num(goal[targetKey]) : null,
    unit,
  });

  const total = Math.round(calories);
  const remaining = target != null ? Math.round(target - calories) : null;
  const banded = Math.round(low) !== Math.round(high);

  return {
    date,
    day_label: dayLabel(date),
    is_past: date < todayIso(),
    is_today: date === todayIso(),
    count: dayRows.length,
    calories: total,
    calories_low: Math.round(low),
    calories_high: Math.round(high),
    target,
    remaining,
    // Preformatted copy fragments. An absent value is '' rather than an em
    // dash, so a blueprint's line degrades to a shorter sentence instead of
    // printing a placeholder into the middle of one.
    calories_label: formatNumber(total),
    count_label: formatNumber(dayRows.length),
    target_label: target == null ? '' : formatNumber(target),
    range_label: banded ? `${formatNumber(Math.round(low))}–${formatNumber(Math.round(high))}` : '',
    remaining_label: remaining == null
      ? ''
      : (remaining < 0 ? `${formatNumber(Math.abs(remaining))} over` : `${formatNumber(remaining)} left`),
    macros: [
      { key: 'calories', label: 'Calories', value: Math.round(calories), target, unit: 'cal' },
      macro('protein_g', 'Protein', 'protein_target_g', 'g'),
      macro('carbs_g', 'Carbs', 'carbs_target_g', 'g'),
      macro('fat_g', 'Fat', 'fat_target_g', 'g'),
    ].filter((m) => m.value != null),
  };
}

// ── 3. weekBars(meals, { anchor, target, window, days }) ───────────────────
// The 7-day window the anchor sits in. Future days are inert (the chart must
// never invite a tap on a day that cannot hold data).
//
// TWO WINDOWS, because two products are right about different things:
//   calendar  the Mon–Sun week CONTAINING the anchor — the default, and what
//             every existing blueprint gets, byte for byte;
//   rolling   the 7 days ENDING at the anchor — prod's reading, where "this
//             week" means the last seven days rather than a calendar row that
//             is two days old on a Tuesday.
//
// It also emits the three READOUTS prod prints beside the chart and no
// blueprint could compute (the bind grammar has no arithmetic): the average
// over LOGGED days, how many of the seven were logged at all, and one thumbnail
// per logged day. `avg` divides by logged days, never by seven — an average
// that counts the days you did not eat is not an average of anything.
function weekBars(rows, args = {}) {
  const anchorIso = isDayString(args.anchor) ? args.anchor : todayIso();
  const anchor = new Date(`${anchorIso}T12:00:00`);
  const span = Math.max(1, Math.trunc(num(args.days) || 7));
  const rolling = String(args.window || 'calendar').toLowerCase() === 'rolling';
  const dow = (anchor.getDay() + 6) % 7; // Monday = 0
  const start = rolling
    ? new Date(anchor.getTime() - (span - 1) * DAY_MS)
    : new Date(anchor.getTime() - dow * DAY_MS);
  const today = todayIso();
  const target = args.target != null ? num(args.target) : null;

  const totals = new Map();
  const counts = new Map();
  const thumbs = new Map();
  for (const r of rows) {
    const d = dayOf(r);
    if (!d) continue;
    totals.set(d, (totals.get(d) || 0) + num(r.calories));
    counts.set(d, (counts.get(d) || 0) + 1);
    if (!thumbs.has(d)) {
      const url = firstPresent(r, PHOTO_SOURCES);
      if (present(url)) thumbs.set(d, url);
    }
  }

  const bars = [];
  const dayThumbs = [];
  // iso → its ORDINAL in the window (0…span-1), so a per-meal thumb can name the
  // day it belongs to without re-deriving the window on the blueprint side.
  const windowDays = new Map();
  let logged = 0;
  let loggedTotal = 0;
  for (let i = 0; i < span; i++) {
    const d = new Date(start.getTime() + i * DAY_MS);
    const iso = isoDay(d);
    windowDays.set(iso, i);
    const value = Math.round(totals.get(iso) || 0);
    const count = counts.get(iso) || 0;
    // A day is LOGGED when it holds a row — not when it holds calories. A
    // zero-calorie entry is a day the user showed up for.
    if (count > 0) { logged += 1; loggedTotal += value; }
    const label = d.toLocaleDateString(DATE_LOCALE, { weekday: 'narrow' }); // LOCKED en-US
    bars.push({
      date: iso,
      label,
      value,
      count,
      logged: count > 0,
      future: iso > today,
      selected: iso === anchorIso,
    });
    if (count > 0 && thumbs.has(iso)) {
      dayThumbs.push({ date: iso, label, image_url: thumbs.get(iso), day_label: dayLabel(iso) });
    }
  }

  // PER-MEAL thumbs — every logged meal IN THE WINDOW that has an image, newest
  // first, one per item_id, capped at 12. `day_thumbs` is one-per-day (a chart's
  // day marker); this is the round-thumbnail RAIL's data — a row of the week's
  // meals — so a blueprint binds a circular-thumbnail collection to `week.thumbs`
  // without a photo derive of its own. Additive: `bars`/`day_thumbs`/labels are
  // untouched.
  const mealThumbs = [];
  const seenItems = new Set();
  const rowMs = (r) => {
    const v = r?.created_at_ms ?? r?.created_at;
    if (typeof v === 'number') return v;
    return Date.parse(v || '') || 0;
  };
  // newest-first, with the row's ORIGINAL feed index breaking ties — so two
  // meals at the same instant order deterministically and IDENTICALLY to Flutter
  // (whose `List.sort` is not stable, so the tiebreak cannot be left implicit).
  const inWindow = rows
    .map((r, i) => ({ r, i }))
    .filter(({ r }) => windowDays.has(dayOf(r)) && present(firstPresent(r, PHOTO_SOURCES)))
    .sort((a, b) => (rowMs(b.r) - rowMs(a.r)) || (a.i - b.i))
    .map(({ r }) => r);
  for (const r of inWindow) {
    const itemId = r.item_id ?? r.id ?? null;
    if (itemId != null) {
      if (seenItems.has(itemId)) continue;
      seenItems.add(itemId);
    }
    const entry = {
      image_url: firstPresent(r, PHOTO_SOURCES),
      item_id: itemId,
      // the LOG CARD identity — so a tap opens the exact card in its session,
      // not just the session. Carried beside item_id (both may be null).
      log_card_id: r.log_card_id ?? null,
      day_index: windowDays.get(dayOf(r)),
    };
    if (present(r.source_session_id)) entry.session_id = r.source_session_id;
    mealThumbs.push(entry);
    if (mealThumbs.length >= 12) break;
  }

  // PER-BAR thumbs — each day's OWN photo meals, so the chart can stack them in
  // that day's bar column instead of a separate rail. `inWindow` is already
  // sorted newest-first with the deterministic tiebreak, so bucketing it by day
  // keeps each bar newest-first and byte-identical to Flutter. Dedup by item_id
  // WITHIN a day, capped per day. Same entry shape as `thumbs` MINUS `day_index`
  // (a bar already IS its day). Additive: `bars` gains a field; nothing above
  // changes, and `thumbs`/`day_thumbs` stay for code/fallbacks that read them.
  const PER_BAR_THUMB_CAP = 3; // tunable — meals stacked vertically under one day's bar
  const perDayThumbs = new Map(); // iso → entry[] (newest-first, ≤ cap, item_id-deduped)
  const perDaySeen = new Map(); // iso → Set(item_id)
  for (const r of inWindow) {
    const iso = dayOf(r);
    let seen = perDaySeen.get(iso);
    if (!seen) { seen = new Set(); perDaySeen.set(iso, seen); }
    const itemId = r.item_id ?? r.id ?? null;
    if (itemId != null) {
      if (seen.has(itemId)) continue;
      seen.add(itemId);
    }
    let list = perDayThumbs.get(iso);
    if (!list) { list = []; perDayThumbs.set(iso, list); }
    if (list.length >= PER_BAR_THUMB_CAP) continue;
    const entry = {
      image_url: firstPresent(r, PHOTO_SOURCES),
      item_id: itemId,
      log_card_id: r.log_card_id ?? null,
    };
    if (present(r.source_session_id)) entry.session_id = r.source_session_id;
    list.push(entry);
  }
  for (const b of bars) b.thumbs = perDayThumbs.get(b.date) || [];

  const avg = logged ? Math.round(loggedTotal / logged) : null;
  return {
    bars,
    target,
    week_start: isoDay(start),
    window_start: isoDay(start),
    window_end: isoDay(new Date(start.getTime() + (span - 1) * DAY_MS)),
    window: rolling ? 'rolling' : 'calendar',
    anchor: anchorIso,
    // the readouts, preformatted for the same reason `todayTotals` preformats:
    // interpolation is lossless, so a number a blueprint puts in its own copy
    // has to arrive already formatted or the caption and the chart disagree
    avg,
    avg_label: avg == null ? '' : formatNumber(avg),
    logged_days: logged,
    total_days: span,
    logged_label: `${formatNumber(logged)}/${formatNumber(span)}`,
    day_thumbs: dayThumbs,
    thumbs: mealThumbs,
  };
}

// ── 4. mealGroups(meals, { date, order }) ──────────────────────────────────
// Grouped by payload.meal in the settings order, each with its own subtotal.
function mealGroups(rows, args = {}) {
  const date = isDayString(args.date) ? args.date.slice(0, 10) : todayIso();
  const order = Array.isArray(args.order) && args.order.length
    ? args.order
    : ['breakfast', 'lunch', 'dinner', 'snack'];
  const dayRows = rows.filter((r) => dayOf(r) === date);

  const buckets = new Map(order.map((k) => [k, []]));
  for (const r of dayRows) {
    const k = String(r.meal || r.meal_type || 'snack').toLowerCase();
    if (!buckets.has(k)) buckets.set(k, []);
    buckets.get(k).push(r);
  }

  const groups = [];
  for (const [key, items] of buckets) {
    if (!items.length) continue;
    items.sort((a, b) => String(a.time || a.created_at || '').localeCompare(String(b.time || b.created_at || '')));
    groups.push({
      key,
      label: key.charAt(0).toUpperCase() + key.slice(1),
      items,
      subtotal: Math.round(items.reduce((s, r) => s + num(r.calories), 0)),
      count: items.length,
    });
  }
  return { date, day_label: dayLabel(date), groups, total: dayRows.length };
}

// ── 5. safetyList(meals) ───────────────────────────────────────────────────
// THE VERDICT VOCABULARY, spelled ONCE. A row's `safety` is one OBJECT —
// `{verdict: eat|caution|avoid, reason}` (the widget's own data_schema) — and
// not a list of flag strings. `eat` is deliberately absent from this table:
// the only honest rendering of "no concern" is NOTHING, because a green chip
// on every safe meal teaches nobody to read the two that are not.
const SAFETY_VERDICT = {
  caution: { rank: 1, tone: 'warn', label: 'Caution' },
  avoid: { rank: 2, tone: 'bad', label: 'Avoid' },
};

/** A row's safety verdict, or null when it carries none worth showing. */
function safetyVerdict(row) {
  const s = row?.safety;
  if (!s || typeof s !== 'object' || Array.isArray(s)) return null;
  const verdict = String(s.verdict || '').toLowerCase();
  const spec = SAFETY_VERDICT[verdict];
  if (!spec) return null;
  return { verdict, reason: String(s.reason ?? '').trim(), ...spec };
}

// Dedup'd avoid/caution verdicts, each already carrying the TONE and LABEL a
// NoticeList renders — the notice list itself knows nothing about "avoid".
// DATA-GATED: an empty result hides the section entirely; the kit must never
// render "no flags" as reassurance.
//
// Keyed by FOOD NAME, not by reason text: the same dish logged three times is
// one line on the watch list, at its WORST verdict, and the reason shown is the
// most recent one — which is why the rows are walked newest-first.
function safetyList(rows) {
  const byName = new Map();
  const newestFirst = [...rows].sort(
    (a, b) => String(b.created_at || '').localeCompare(String(a.created_at || '')),
  );
  for (const r of newestFirst) {
    const v = safetyVerdict(r);
    if (!v) continue;
    const name = String(r.name ?? '').trim() || 'Item';
    const key = name.toLowerCase();
    const hit = byName.get(key);
    if (hit && SAFETY_VERDICT[hit.level].rank >= v.rank) { hit.count += 1; continue; }
    byName.set(key, {
      level: v.verdict,
      tone: v.tone,
      label: v.label,
      name,
      reason: v.reason,
      // The row's one readable line: WHAT was flagged, then why. `label` is the
      // eyebrow above it, so the word is never lost when the reason is empty.
      text: v.reason ? `${name} · ${v.reason}` : name,
      count: (hit?.count ?? 0) + 1,
    });
  }
  return [...byName.values()]
    .sort((a, b) => SAFETY_VERDICT[b.level].rank - SAFETY_VERDICT[a.level].rank);
}

// ── 6. distinctPhotos(pieces, { key, fallback_key, order, model_url }) ─────
// One entry per capture SESSION (the Photos tab is session-bound: a photo
// spans many pieces, so there is no item id). grid_id = session || photo path.
// Each row also carries the two presentation fields a session card binds:
// `piece_count`, and `model_label` ("Model") on the ONE session holding the
// pinned model photo — so the card can badge it without knowing what a model
// photo is.
//
// The model row is found in the FEED, not through a selector: the natural
// spelling (`args.model` → the modelPhoto selector) is a cycle, because
// modelPhoto resolves itself against this very list. `args.model_url` stays as
// an explicit override for a widget that names its cover some other way.
function distinctPhotos(rows, args = {}, feed = {}) {
  const key = args.key || 'source_session_id';
  const fallback = args.fallback_key || 'photo_url';
  // The FEED wins over `args.model_url`: an arg naming a selector that has not
  // resolved yet arrives as its own path string, and a literal like
  // "model.photo_url" would match nothing and silently unlabel the row.
  const model = modelPhoto(Array.isArray(feed.folded) ? feed.folded : [], {});
  const modelUrl = model?.photo_url || (args.model_url ? String(args.model_url) : null);
  const modelSession = model?.session_id ?? null;
  const seen = new Map();
  for (const r of rows) {
    const gridId = String(r[key] || r[fallback] || r.entry_id || '');
    if (!gridId) continue;
    const hit = seen.get(gridId);
    if (hit) {
      hit.count += 1;
      if (!hit.photo_url) hit.photo_url = r.photo_url || r.source_photo_url || '';
      // Job-by-Contract (cardgap): carry the session's contract region/lifecycle from
      // whichever piece anchors the contract (only ONE piece of a capture carries it) so
      // the derived session card can draw the in_progress spinner / failed "!" for an
      // extract launch. Only-when-present → a non-contract session is byte-identical (BC).
      if (hit.region == null && r.region != null) hit.region = r.region;
      if (hit.contract_uid == null && r.contract_uid != null) {
        hit.contract_uid = r.contract_uid;
        if (r.contract_lifecycle != null) hit.contract_lifecycle = r.contract_lifecycle;
        if (r.contract_progress != null) hit.contract_progress = r.contract_progress;
      }
      continue;
    }
    seen.set(gridId, {
      grid_id: gridId,
      session_id: r[key] || null,
      photo_url: r.photo_url || r.source_photo_url || r.image_url || '',
      created_at: r.created_at || null,
      count: 1,
      name: r.capture_name || '',
      // cardgap: the contract fields the session card's region badge reads (BC when absent).
      ...(r.region != null ? { region: r.region } : null),
      ...(r.contract_uid != null ? { contract_uid: r.contract_uid } : null),
      ...(r.contract_lifecycle != null ? { contract_lifecycle: r.contract_lifecycle } : null),
      ...(r.contract_progress != null ? { contract_progress: r.contract_progress } : null),
    });
  }
  // SESSION first, photo path only as the fallback: two captures can share a
  // source photo, and labelling both would claim the user pinned two models.
  const isModel = (r) => (modelSession != null
    ? String(r.session_id) === String(modelSession)
    : (modelUrl != null && r.photo_url === modelUrl));
  const out = [...seen.values()].map((r) => ({
    ...r,
    piece_count: r.count,
    model_label: isModel(r) ? (args.model_label || 'Model') : '',
  }));
  if (args.order === 'earliest-first') {
    out.sort((a, b) => String(a.created_at || '').localeCompare(String(b.created_at || '')));
  } else {
    out.sort((a, b) => String(b.created_at || '').localeCompare(String(a.created_at || '')));
  }
  return out;
}

// ── 7. modelPhoto(items, { photos }) ───────────────────────────────────────
// The user's pinned on-model photo: the newest `type:'model'` row, resolved
// against the derived photo list so the Photos grid can badge its tile.
function modelPhoto(rows, args = {}) {
  const photos = Array.isArray(args.photos) ? args.photos : [];
  const models = rows.filter((r) => String(r.type) === 'model');
  const latest = models[0] || null;
  // No explicit pin yet — the widget's promise is "the first photo becomes your
  // model" (photoGrid's own empty copy). Fall back to the earliest capture so the
  // build-a-look model card + Create look get a real photo instead of a silhouette.
  // `photos` is pre-sorted to the caller's `order` (my-wardrobe passes earliest-first).
  // When there is no photo at all the card falls to the "Add your model photo"
  // prompt (blueprint `modelPrompt`), never a bare silhouette.
  if (!latest) return photos[0] || null;
  const url = latest.photo_url || latest.image_url || '';
  const match = photos.find((p) => p.photo_url === url || p.session_id === latest.source_session_id);
  return {
    photo_url: url,
    session_id: latest.source_session_id || match?.session_id || null,
    grid_id: match?.grid_id || latest.source_session_id || url,
    set_at: latest.created_at || null,
  };
}

// ── 8. foldByKey(rows, { key }) ────────────────────────────────────────────
// Generic "one row per key, newest wins" — the escape hatch a blueprint uses
// when its identity is not the fold key (e.g. a second grouping axis).
function foldByKey(rows, args = {}) {
  const key = args.key || 'id';
  const seen = new Map();
  for (const r of rows) {
    const k = String(r[key] ?? '');
    if (!k || seen.has(k)) continue;
    seen.set(k, r);
  }
  return [...seen.values()];
}

// ── 9. expiryStatus(items, { soon_days }) ──────────────────────────────────
// ROW ANNOTATION. Each item gains the three fields a generic card binds to
// show expiry without knowing what expiry is:
//   status        ok | soon | expired | nodate
//   status_tone   good | warn | bad | text-3   (a kit tone name)
//   status_label  "" | "Expiring soon" | "Expired" | "No date"
// `ok` deliberately carries an EMPTY label so a healthy row shows no badge.
const EXPIRY_TONE = { ok: 'good', soon: 'warn', expired: 'bad', nodate: 'text-3' };
const EXPIRY_LABEL = { ok: '', soon: 'Expiring soon', expired: 'Expired', nodate: 'No date' };

function expiryStatus(rows, args = {}) {
  const soonDays = num(args.soon_days) || 30;
  return rows.map((r) => {
    const status = expiryOf(r, soonDays);
    return { ...r, status, status_tone: EXPIRY_TONE[status], status_label: EXPIRY_LABEL[status] };
  });
}

// ── 10. mealCard(items) ────────────────────────────────────────────────────
// ROW ANNOTATION. Turns an estimate into the honest strings a card renders:
//   calorie_label     "~1770 · 1520–2070"  (the range only when it differs)
//   confidence_level  0..3 — a Gauge's `value` over 3 segments; 0 = unstated
//   macro_label       "P 92 · C 171 · F 82" (only the macros that exist)
//   safety_tone       "bad" | "warn" | null   ┐ the row's verdict, so a card
//   safety_text       "Avoid · <reason>"|null ┘ can badge it in one line
// An `eat` verdict — like a missing one — badges NOTHING (`safetyList`'s table
// is the one place that rule is written).
// A guess must LOOK like a guess — that rule lives here, in the data, so every
// runtime and every card spells it the same way.
const CONFIDENCE_LEVEL = { high: 3, medium: 2, med: 2, low: 1 };

function mealCard(rows) {
  return rows.map((r) => {
    const cal = Math.round(num(r.calories));
    const low = r.calories_low == null ? null : Math.round(num(r.calories_low));
    const high = r.calories_high == null ? null : Math.round(num(r.calories_high));
    // Through the SAME formatter the hero and the chart use — a 1010-cal meal
    // must not read "1010" on the card while the day total reads "1,770".
    const band = low != null && high != null && high !== low
      ? ` · ${formatNumber(low)}–${formatNumber(high)}`
      : '';
    const macros = [
      r.protein_g != null ? `P ${formatNumber(Math.round(num(r.protein_g)))}` : null,
      r.carbs_g != null ? `C ${formatNumber(Math.round(num(r.carbs_g)))}` : null,
      r.fat_g != null ? `F ${formatNumber(Math.round(num(r.fat_g)))}` : null,
    ].filter(Boolean);

    const safety = safetyVerdict(r);

    return {
      ...r,
      calorie_label: r.calories == null ? '' : `~${formatNumber(cal)}${band}`,
      confidence_level: CONFIDENCE_LEVEL[String(r.confidence || '').toLowerCase()] ?? (r.confidence ? 1 : 0),
      macro_label: macros.join(' · '),
      safety_tone: safety ? safety.tone : null,
      safety_text: safety ? (safety.reason ? `${safety.label} · ${safety.reason}` : safety.label) : null,
    };
  });
}

// ── 11. closetStats(items, { photos }) ─────────────────────────────────────
// PROJECTION — the three honest top-line counts of a wardrobe, in the LIST
// shape a `metric` StatTileRow binds (`[{key,label,value}]`).
//
// No row carries a `target`, and that is the point: a count is not a goal. The
// tile's thin value-against-target bar is target-gated, so a targetless row
// draws a number and nothing else — inventing "7 of 20 pieces" would be the
// kit promising the user a quota this widget has never asked them to fill.
//
// The photo count is PASSED IN (`args.photos` → the `photos` selector) rather
// than recomputed: one capture session spans many cutouts, and which rows
// collapse into one photo is `distinctPhotos`'s rule. A second spelling of
// that rule here is exactly how a header and the Photos tab under it start
// disagreeing about how many photos there are. (`modelPhoto` reads the same
// selector the same way, for the same reason.)
function closetStats(rows, args = {}) {
  const photos = Array.isArray(args.photos) ? args.photos : [];
  const countOf = (type) => rows.filter((r) => String(r?.type) === type).length;
  return [
    { key: 'pieces', label: 'Pieces', value: countOf('piece') },
    { key: 'outfits', label: 'Outfits', value: countOf('outfit') },
    { key: 'photos', label: 'Photos', value: photos.length },
  ];
}

// ══ the generic family ═════════════════════════════════════════════════════
// Everything below is shape-generic: named for what it COMPUTES, not for the
// widget that first needed it, so the next widget adds zero derives.

/**
 * PRESENT — what a derive calls "this field has a value".
 * Deliberately not JS truthiness: `0` and `false` are values a payload
 * legitimately carries, while `''`, `[]` and `{}` are the three ways a feed
 * spells "not generated yet".
 */
function present(v) {
  if (v == null) return false;
  if (typeof v === 'string') return v.trim() !== '';
  if (Array.isArray(v)) return v.length > 0;
  if (typeof v === 'object') return Object.keys(v).length > 0;
  return true;
}

/** True when `obj` itself is present, or (with `anyOf`) any of its fields is. */
function presentIn(obj, anyOf) {
  if (!Array.isArray(anyOf) || !anyOf.length) return present(obj);
  if (!obj || typeof obj !== 'object') return false;
  return anyOf.some((f) => present(getPath(obj, f)));
}

/** The first path whose value is present, or ''. */
function firstPresent(row, paths) {
  for (const p of paths) {
    const v = getPath(row, p);
    if (present(v)) return v;
  }
  return '';
}

// ── 12. hasField(rows, { path, any_of, as }) ───────────────────────────────
// ROW ANNOTATION — the PRESENCE PREDICATE `where` cannot express. A `where`
// clause matches a value; this answers "is there a value at this nested path
// at all", which is the question a feed with optional sub-objects actually
// asks (a platform key that EXISTS but is empty means "not generated yet").
function hasField(rows, args = {}, feed, report) {
  const path = args.path || args.field;
  const as = args.as || 'has_value';
  const anyOf = Array.isArray(args.any_of) ? args.any_of : null;
  return rows.map((r) => annotate(r, { [as]: presentIn(path ? getPath(r, path) : r, anyOf) }, report));
}

// ── 13. nonEmptyPath(rows, { path, keys, any_of, as }) ─────────────────────
// ROW ANNOTATION — the same predicate over a MAP of sub-objects: which keys of
// `payload.<path>` actually carry content. Emits the present keys as a list
// (bind it as a badge row), a per-key map (`when: "item.present_map.tiktok"`)
// and a count.
function nonEmptyPath(rows, args = {}, feed, report) {
  const path = args.path || null;
  const as = args.as || 'present';
  const anyOf = Array.isArray(args.any_of) ? args.any_of : null;
  const wanted = Array.isArray(args.keys) && args.keys.length ? args.keys : null;
  return rows.map((r) => {
    const obj = path ? getPath(r, path) : r;
    const keys = wanted || (obj && typeof obj === 'object' && !Array.isArray(obj) ? Object.keys(obj) : []);
    const map = {};
    const list = [];
    for (const k of keys) {
      const ok = presentIn(getPath(obj, k), anyOf);
      map[k] = ok;
      if (ok) list.push(k);
    }
    return annotate(r, { [as]: list, [`${as}_map`]: map, [`${as}_count`]: list.length }, report);
  });
}

// ── 14. facetCounts(rows, { field, min, limit, ladder, labels, other }) ────
// PROJECTION — the chip set a feed EARNS: distinct values of one field with
// their counts, only those occurring at least `min` times, capped at `limit`,
// labelled with the first-seen original casing. `distinct` has no count, no
// floor and no cap, which is why a widget could not build its own chips.
//
// A RAW ENUM IS NOT A LABEL, AND ONE CONCEPT IS NOT ONE SPELLING. The feed
// writes `lecture`, `lecture_capture` and `Lecture` for the same thing, so the
// earned chip row read `meeting · lecture · lecture_capture · recording` where
// the bundle it replaces reads `Meetings · Lectures · Recordings`. A blueprint
// cannot fix either half by hand: authoring the option list buys the casing by
// LOSING the earned-chip rule (a user who has never recorded a lecture must not
// be shown a Lecture chip), which is the whole reason the list is bound.
//
//   `ladder`  [{match, key, label?}] — the first rung whose regex matches the
//             row's value (lowercased, `_`→space, so `lecture_capture` reads as
//             the words it means) decides the facet's CANONICAL key; rows that
//             match no rung keep their own value as key, or land in `other`
//             when one is named. A rung with an illegal pattern is dropped, not
//             thrown — one bad regex must not empty a chip bar.
//   `labels`  {canonical_key: display} — a rung's own `label` wins over it, and
//             it wins over the first-seen casing.
//
// `value` stays THE FILTER TOKEN, never the label: a chip filters rows by the
// value they carry, so a facet that merged genuinely DIFFERENT tokens emits the
// LIST of them (a list value is an OR inside one dimension) and a facet holding
// one token emits the string it always did. Mere casing was already one token
// before any of this (the key has always been the lowercased value), so a
// laddered `field_notes` and an un-laddered `Watercolor`/`watercolor` both keep
// the plain first-seen string they emit today.
function facetLadder(spec) {
  const out = [];
  for (const rung of Array.isArray(spec) ? spec : []) {
    if (!rung || typeof rung !== 'object') continue;
    const key = rung.key == null ? '' : String(rung.key);
    const match = rung.match == null ? '' : String(rung.match);
    if (!key || !match) continue;
    let re = null;
    try { re = new RegExp(match, 'i'); } catch { continue; }
    out.push({ re, key, label: rung.label == null ? null : String(rung.label) });
  }
  return out;
}

function facetCounts(rows, args = {}) {
  const field = args.field || 'category';
  const min = args.min != null ? num(args.min) : 1;
  const limit = args.limit != null ? num(args.limit) : 0;
  const ladder = facetLadder(args.ladder);
  const labels = args.labels && typeof args.labels === 'object' && !Array.isArray(args.labels) ? args.labels : {};
  const other = args.other == null ? null : String(args.other);
  const seen = new Map();
  rows.forEach((r, i) => {
    const raw = getPath(r, field);
    if (!present(raw)) return;
    const spelling = String(raw).trim();
    const probe = spelling.toLowerCase().replace(/_/g, ' ');
    const rung = ladder.find((l) => l.re.test(probe)) || null;
    const k = rung ? rung.key : (other || spelling.toLowerCase());
    const token = spelling.toLowerCase();
    const hit = seen.get(k);
    if (hit) {
      hit.count += 1;
      if (!hit.tokens.has(token)) hit.tokens.set(token, spelling);
      return;
    }
    seen.set(k, {
      key: k,
      label: (rung && rung.label) || labels[k] || spelling,
      tokens: new Map([[token, spelling]]),
      count: 1,
      first: i,
    });
  });
  const out = [...seen.values()]
    .filter((f) => f.count >= min)
    .sort((a, b) => (b.count - a.count) || (a.first - b.first))
    .map((f) => {
      const tokens = [...f.tokens.values()];
      return { key: f.key, value: tokens.length > 1 ? tokens : tokens[0], label: f.label, count: f.count };
    });
  return limit > 0 ? out.slice(0, limit) : out;
}

// ── 15. multiKeyCounts(rows, { path, keys, any_of }) ───────────────────────
// PROJECTION — counts where ONE ROW LANDS IN MANY BUCKETS (a post published to
// three platforms counts three times). `statusCounts`-shaped output, so a
// StatTileRow binds it the same way, plus `buckets[]` for a chip bar.
function multiKeyCounts(rows, args = {}) {
  const path = args.path || null;
  const anyOf = Array.isArray(args.any_of) ? args.any_of : null;
  const wanted = Array.isArray(args.keys) && args.keys.length ? args.keys : null;
  const counts = {};
  const order = [];
  for (const r of rows) {
    const obj = path ? getPath(r, path) : r;
    const keys = wanted || (obj && typeof obj === 'object' && !Array.isArray(obj) ? Object.keys(obj) : []);
    for (const k of keys) {
      if (!(k in counts)) { counts[k] = 0; order.push(k); }
      if (presentIn(getPath(obj, k), anyOf)) counts[k] += 1;
    }
  }
  const keys = wanted || order;
  return {
    ...counts,
    total: rows.length,
    buckets: keys.map((k) => ({ key: k, label: k, count: counts[k] || 0 })),
  };
}

// ── 16. groupByRoot(rows, { parent, id, group_key, singleton_where }) ──────
// PROJECTION — LINEAGE grouping. `foldByKey` groups by a flat key; this walks a
// PARENT POINTER to its root (cycle-guarded), so a variation made from a
// variation lands in the same group as the original. `singleton_where` rows
// opt out and stand alone (a video among stills).
//
// IT EMITS ONE FLAT ROW PER LINEAGE, not a bucket — the same shape `buildGroup`
// defines for the `group-into-one` fold: the REPRESENTATIVE spread at the top
// level, with the members hanging off it as `entries[]`.
//
// That shape is load-bearing rather than cosmetic. A grid's `rowsFrom`
// (collections.jsx) flattens any array whose `[0].items` is an array — that is
// how a `{op:'group'}` selector renders flat — so emitting `{key, items[]}`
// here made the wall draw one cell PER PIECE, i.e. exactly the grouping this
// derive exists to perform, undone one layer down. The field is `entries` for
// the same reason it is `entries` on a folded group: `items` means "this is a
// bucket, flatten me".
//
// The representative is the OLDEST member (`top` — the original), because that
// is what a lineage tile shows and whose `entry_id` `open_item` must carry.
// `count` is the lineage size and `version_count` the number of REDESIGNS
// (count − 1): the badge copy is "N 复刻", and the bind grammar deliberately
// has no arithmetic, so a count a widget needs is a count a derive emits.
// `top` / `latest` / `root` stay on the row for a page that wants the ends of
// the lineage explicitly.
function groupByRoot(rows, args = {}) {
  const parent = args.parent || 'parent_id';
  const idField = args.id || 'id';
  const groupKey = args.group_key || null;
  const singleton = args.singleton_where || null;

  const byId = new Map();
  for (const r of rows) {
    const k = getPath(r, idField);
    if (present(k) && !byId.has(String(k))) byId.set(String(k), r);
  }
  const rootOf = (row) => {
    let cur = row;
    const seen = new Set();
    for (;;) {
      const self = String(getPath(cur, idField) ?? '');
      if (self) {
        if (seen.has(self)) return cur; // cycle — this row IS the root
        seen.add(self);
      }
      const up = getPath(cur, parent);
      if (!present(up) || String(up) === self) return cur;
      const next = byId.get(String(up));
      if (!next) return cur; // the parent never landed — stop where we are
      cur = next;
    }
  };

  const groups = new Map();
  for (const r of rows) {
    const alone = singleton && matchesWhere(r, singleton);
    const root = alone ? r : rootOf(r);
    const key = alone
      ? `row:${getPath(r, idField) ?? r.entry_id ?? ''}`
      : String((groupKey && getPath(root, groupKey)) || `${idField}:${getPath(root, idField) ?? ''}`);
    if (!groups.has(key)) groups.set(key, { key, root, items: [] });
    groups.get(key).items.push(r);
  }

  const at = (r) => String(r?.created_at || '');
  return [...groups.values()]
    .map((g) => {
      const items = [...g.items].sort((a, b) => at(a).localeCompare(at(b)));
      const top = items[0] || null;
      const latest = items[items.length - 1] || null;
      return {
        ...(top || {}),
        key: g.key,
        group_key: g.key,
        root: g.root,
        entries: items,
        count: items.length,
        version_count: Math.max(0, items.length - 1),
        top,
        latest,
      };
    })
    .sort((a, b) => at(b.latest).localeCompare(at(a.latest)));
}

// ── 17. latestChildStatus(rows, { child, field, tones, labels, … }) ────────
// ROW ANNOTATION — the row's status read off the NEWEST entry of one of its
// child streams (the folded `checks[]` a `field-merge` fold attaches), falling
// back to a row-level path when the stream is empty. `expiryStatus` is this
// same shape hard-wired to dates; this is the generic one.
//
//   status · status_tone · status_label · status_open   (renamed by `as`)
//
// `skip_when` is the "this row is not assessed at all" gate (a plant spotted in
// the wild is never sick) — it lands on the default without touching the data.
function latestChildStatus(rows, args = {}, feed, report) {
  const child = args.child || 'children';
  const field = args.field || 'status';
  const as = args.as || 'status';
  const tones = args.tones || {};
  const labels = args.labels || {};
  const openValues = new Set((args.open_values || []).map((v) => String(v).toLowerCase()));
  const fallbackPath = args.fallback_path || null;
  const at = (r) => String(getPath(r, args.child_order || 'created_at') || r?.created_at || '');

  return rows.map((r) => {
    const skip = args.skip_when && matchesWhere(r, args.skip_when);
    const list = Array.isArray(getPath(r, child)) ? getPath(r, child) : [];
    const newest = list.length
      ? [...list].sort((a, b) => at(a).localeCompare(at(b)))[list.length - 1]
      : null;
    let value = newest ? getPath(newest, field) : null;
    if (!present(value) && fallbackPath) value = getPath(r, fallbackPath);
    const key = present(value) && !skip ? String(value).toLowerCase() : '';
    return annotate(r, {
      [as]: key,
      [`${as}_tone`]: tones[key] ?? args.default_tone ?? 'text-3',
      [`${as}_label`]: labels[key] ?? args.default_label ?? '',
      [`${as}_open`]: Boolean(key) && openValues.has(key),
      [`${as}_at`]: newest?.created_at || null,
    }, report);
  });
}

// ── 18. rankStatus(rows, { fields, rank, tones, labels, force_when }) ──────
// ROW ANNOTATION — the WORST of several ranked enum fields, plus its tone and
// label. Generic because the ranking is data: `{safe:0, mild:1, moderate:2,
// toxic:3}` is an argument, not a rule of the runtime.
//
// `force_when` is the low-confidence gate every safety verdict needs: when the
// row matches, the verdict is forced to `value` UNLESS it already reads one of
// `unless` — never a green verdict on a guess.
function rankStatus(rows, args = {}, feed, report) {
  const fields = Array.isArray(args.fields) ? args.fields : [];
  const rank = args.rank || {};
  const tones = args.tones || {};
  const labels = args.labels || {};
  const as = args.as || 'status';
  const unknown = args.unknown || 'unknown';
  const force = args.force_when || null;
  const forceUnless = new Set((force?.unless || []).map((v) => String(v).toLowerCase()));

  return rows.map((r) => {
    let best = null;
    let bestRank = -Infinity;
    for (const f of fields) {
      const raw = getPath(r, f);
      if (!present(raw)) continue;
      const k = String(raw).toLowerCase();
      const score = rank[k] != null ? num(rank[k]) : -1;
      if (score > bestRank) { bestRank = score; best = k; }
    }
    let value = best || unknown;
    if (force && matchesWhere(r, force.where || {}) && !forceUnless.has(value)) {
      value = String(force.value || unknown).toLowerCase();
      bestRank = rank[value] != null ? num(rank[value]) : -1;
    }
    return annotate(r, {
      [as]: value,
      [`${as}_rank`]: Number.isFinite(bestRank) ? bestRank : -1,
      [`${as}_tone`]: tones[value] ?? args.default_tone ?? 'text-3',
      [`${as}_label`]: labels[value] ?? args.default_label ?? '',
    }, report);
  });
}

// ── 19. unionStream(rows, { sources, synthetic, sort_by, sort }) ───────────
// PROJECTION — several child streams merged into ONE ordered timeline, each
// row tagged with the stream it came from and carrying its own `entry_id` (so
// the host can delete exactly it). `synthetic` rows are computed from the OWNER
// row — the "Identified" marker a timeline needs and no feed row carries — and
// deliberately have `entry_id: null`, which is how a card knows not to offer a
// delete on them.
function unionStream(rows, args = {}) {
  const sources = (Array.isArray(args.sources) ? args.sources : [])
    .map((s) => (typeof s === 'string' ? { from: s } : s))
    .filter((s) => s && s.from);
  const sortBy = args.sort_by || 'created_at';
  const desc = String(args.sort || 'desc').toLowerCase() !== 'asc';

  const out = [];
  for (const owner of rows) {
    for (const src of sources) {
      /**
       * `{from: "self"}` — the OWNER ROW as a source of its own.
       *
       * A timeline needs rows no child stream carries: plant-expert's
       * "Identified" marker is the plant's own `created_at`, and "Diagnosed ·
       * {health.title}" is its own `health`. `synthetic[]` already computes
       * exactly that, but off to the side, so a blueprint could not say WHERE
       * in the source order the marker belongs. Spelling it as a source keeps
       * one declared list — same fields, same `entry_id: null` (which is how a
       * card knows not to offer a delete on a row that is not an event).
       */
      if (src.from === 'self') {
        if (src.require && !present(getPath(owner, src.require))) continue;
        const suffix = src.title_from ? getPath(owner, src.title_from) : null;
        out.push({
          stream: src.stream || 'self',
          owner_id: owner.entry_id ?? owner.id ?? null,
          entry_id: null,
          id: null,
          title: present(suffix) ? `${src.title || ''}${suffix}` : (src.title || ''),
          created_at: getPath(owner, src.date || src.at_from || 'created_at') || null,
          synthetic: true,
        });
        continue;
      }
      const list = getPath(owner, src.from);
      if (!Array.isArray(list)) continue;
      for (const child of list) {
        const title = src.title_from ? getPath(child, src.title_from) : null;
        out.push({
          ...child,
          stream: src.stream || src.from,
          owner_id: owner.entry_id ?? owner.id ?? null,
          title: present(title) ? `${src.title_prefix || ''}${title}` : (src.title || child?.title || ''),
          synthetic: false,
        });
      }
    }
    for (const syn of (Array.isArray(args.synthetic) ? args.synthetic : [])) {
      if (syn?.require && !present(getPath(owner, syn.require))) continue;
      const suffix = syn?.title_from ? getPath(owner, syn.title_from) : null;
      out.push({
        stream: syn?.stream || 'event',
        owner_id: owner.entry_id ?? owner.id ?? null,
        entry_id: null,
        id: null,
        title: present(suffix) ? `${syn.title || ''}${suffix}` : (syn?.title || ''),
        created_at: getPath(owner, syn?.at_from || 'created_at') || null,
        synthetic: true,
      });
    }
  }
  const at = (r) => String(getPath(r, sortBy) ?? r?.created_at ?? '');
  out.sort((a, b) => (desc ? at(b).localeCompare(at(a)) : at(a).localeCompare(at(b))));
  return out;
}

// ── 20. firstPhoto(rows, { sources, as }) ──────────────────────────────────
// ROW ANNOTATION — THE COVER LADDER, once, for every widget: the first present
// ref among an ordered list of paths. Every bundle re-implements this by hand
// (`images[0] || image_url || photo_url || media_url`) and every one of them
// spells it slightly differently.
const PHOTO_SOURCES = ['images[0]', 'image_url', 'photo_url', 'thumb_url', 'media_url'];

function firstPhoto(rows, args = {}, feed, report) {
  const sources = Array.isArray(args.sources) && args.sources.length ? args.sources : PHOTO_SOURCES;
  const as = args.as || 'cover';
  return rows.map((r) => annotate(r, { [as]: firstPresent(r, sources) }, report));
}

// ── 21. mediaState(rows, { sources, as }) ──────────────────────────────────
// ROW ANNOTATION — the TRI-STATE a media handle actually has, which nothing in
// the bind grammar can tell apart:
//   resolved  the host has re-signed it (http(s)/data/blob) ⇒ safe to offer
//   pending   still a bare `media://` handle ⇒ say "preparing", offer nothing
//   none      no ref at all ⇒ draw NOTHING (never a dead affordance)
const RESOLVED_RE = /^(https?:|data:|blob:)/i;

function mediaState(rows, args = {}, feed, report) {
  const sources = Array.isArray(args.sources) && args.sources.length ? args.sources : PHOTO_SOURCES;
  const as = args.as || 'media';
  return rows.map((r) => {
    const url = firstPresent(r, sources);
    const state = !present(url) ? 'none' : (RESOLVED_RE.test(String(url)) ? 'resolved' : 'pending');
    return annotate(r, {
      [`${as}_url`]: url,
      [`${as}_state`]: state,
      [`${as}_resolved`]: state === 'resolved',
    }, report);
  });
}

// ── 22. dateBuckets(rows, { field, fallback_field, order }) ────────────────
// PROJECTION — rows bucketed by RELATIVE day (`Today` / `Yesterday` / `Mon, Jul
// 28`), buckets emitted in feed order. `mealGroups` buckets by a fixed enum;
// this buckets by the calendar, which is the other half every log-shaped widget
// needs.
function dateBuckets(rows, args = {}) {
  const field = args.field || 'created_at';
  const fallback = args.fallback_field || 'created_at';
  const buckets = new Map();
  for (const r of rows) {
    const raw = present(getPath(r, field)) ? getPath(r, field) : getPath(r, fallback);
    const iso = present(raw) ? isoDay(raw) : '';
    const key = iso || 'other';
    if (!buckets.has(key)) {
      buckets.set(key, { key, date: iso || null, label: iso ? dayLabel(iso) : (args.other_label || 'Other'), items: [] });
    }
    buckets.get(key).items.push(r);
  }
  const out = [...buckets.values()].map((b) => ({ ...b, count: b.items.length }));
  if (String(args.order || '').toLowerCase() === 'asc') {
    out.sort((a, b) => String(a.date || '').localeCompare(String(b.date || '')));
  }
  return out;
}

// ── 23. pickRepresentative(groups, { where, prefer_nonempty }) ─────────────
// PROJECTION over a `group-into-one` fold — filter each group's `entries[]` and
// RECOMPUTE its representative inside the filtered subset, dropping groups that
// keep nothing. This is what makes a media filter honest: the Video tab shows
// the same session under its video cover, not under the image cover the
// unfiltered fold happened to pick.
function pickRepresentative(groups, args = {}) {
  const prefer = Array.isArray(args.prefer_nonempty) ? args.prefer_nonempty : [];
  const where = args.where || null;
  const out = [];
  for (const g of groups) {
    const rows = (Array.isArray(g?.entries) ? g.entries : []).filter((r) => (where ? matchesWhere(r, where) : true));
    if (!rows.length) continue;
    out.push(buildGroup({ key: g.group_key ?? g.key, session_id: g.session_id ?? null, rows }, prefer));
  }
  return out;
}

// ── 24. resolveVisuals(rows, { captions, images, as, … }) ──────────────────
// ROW ANNOTATION — ZIP the authored captions against the host-resolved urls.
//
// A feed carries two parallel arrays: `visuals[] = {image_url, caption,
// captured_at}` is what the model wrote, `images[]` is the same pictures after
// the host re-signed the `media://` handles. They line up BY POSITION and by
// nothing else, and a bind can read one array or the other but never both — so
// a page that binds `images` gets pictures with no captions, and one that binds
// `visuals` gets captions over handles no runtime can load. Zipping is the
// whole derive.
//
// A figure with no loadable url is DROPPED, not drawn empty: prod's
// `figureHtml()` hides the whole `<figure>` on error (ux_rule: 缺图时隐藏图区,
// 不渲染坏格), and an unlabelled gap is worse than one fewer slide. A picture
// with no caption keeps its ordinal name ("Visual 3") so a carousel still has
// something to say.
const VISUAL_URL_FIELDS = ['image_url', 'url', 'src', 'photo_url'];

/** The url of one member of either array (a bare string, or an object). */
function visualUrl(v) {
  if (v == null) return '';
  if (typeof v === 'string') return v;
  return firstPresent(v, VISUAL_URL_FIELDS);
}

function resolveVisuals(rows, args = {}, feed, report) {
  const captionsPath = args.captions || 'visuals';
  const imagesPath = args.images || 'images';
  const as = args.as || 'figures';
  const captionField = args.caption_field || 'caption';
  const label = args.label || 'Visual';

  return rows.map((r) => {
    const captions = Array.isArray(getPath(r, captionsPath)) ? getPath(r, captionsPath) : [];
    const images = Array.isArray(getPath(r, imagesPath)) ? getPath(r, imagesPath) : [];
    const out = [];
    for (let i = 0; i < Math.max(captions.length, images.length); i++) {
      const authored = captions[i];
      // The RESOLVED url wins: it is the same picture, re-signed. The authored
      // one is the fallback for a feed the host has not touched yet.
      const url = visualUrl(images[i]) || visualUrl(authored);
      if (!present(url)) continue;
      const caption = String(getPath(authored, captionField) ?? '').trim() || `${label} ${i + 1}`;
      out.push({
        index: i,
        image_url: url,
        src: url,
        // `name` is what the media components print over a slide; `caption` is
        // what a figure block binds. Same string, two vocabularies.
        name: caption,
        caption,
        captured_at: getPath(authored, args.at_field || 'captured_at') ?? null,
      });
    }
    return annotate(r, { [as]: out, [`${as}_count`]: out.length }, report);
  });
}

// ── 25. dueTimeline(rows, { task, owner, due, status, … }) ─────────────────
// PROJECTION — owner-bound to-dos with dates, in the shape `Timeline` already
// draws (`{node, primary, secondary, tone}`).
//
// The component exists; what was missing is the projection, and every rule in
// it is a judgement the kit must not make on its own:
//   · only DATED rows — an undated to-do has no place on a time axis;
//   · only when at least `min_dated` of them are dated (default 2), because a
//     single node is a dot, not a timeline, and prod draws nothing;
//   · sorted ascending and capped, because a strip reads left to right and a
//     20-item strip reads as nothing;
//   · tone by status, with OVERDUE computed rather than authored: a row is
//     late if its date has passed and it is not done, and no feed carries that
//     because it changes without the feed changing.
// `done_matches` is a regex so a feed in any language can say "done" its own
// way — prod's `statusKind()` matches ten languages' literals.
const DUE_TONES = { done: 'good', overdue: 'bad', due: 'accent' };

function dueTimeline(rows, args = {}) {
  const taskField = args.task || 'task';
  const ownerField = args.owner || 'owner';
  const dueField = args.due || 'due';
  const statusField = args.status || 'status';
  const limit = args.limit != null ? Math.max(0, Math.trunc(num(args.limit))) : 6;
  const minDated = args.min_dated != null ? Math.max(0, Math.trunc(num(args.min_dated))) : 2;
  const tones = { ...DUE_TONES, ...(args.tones || {}) };
  const pattern = args.date_format || 'MMM d';
  let done;
  try {
    done = new RegExp(String(args.done_matches || 'done|complete|finished|closed'), 'i');
  } catch {
    done = /done|complete|finished|closed/i;
  }
  const today = todayIso();

  const dated = [];
  for (const r of rows) {
    const raw = getPath(r, dueField);
    if (!present(raw)) continue;
    const iso = localDay(raw);
    if (!iso) continue;
    const status = String(getPath(r, statusField) ?? '').trim();
    const isDone = done.test(status);
    const kind = isDone ? 'done' : (iso < today ? 'overdue' : 'due');
    const ownerName = String(getPath(r, ownerField) ?? '').trim();
    const dateLabel = formatDate(iso, pattern);
    dated.push({
      node: formatDate(iso, args.node_format || 'd'),
      primary: String(getPath(r, taskField) ?? '').trim(),
      secondary: [dateLabel, ownerName].filter(Boolean).join(' · '),
      tone: tones[kind] ?? 'accent',
      kind,
      done: isDone,
      date: iso,
      date_label: dateLabel,
      owner: ownerName,
      status,
    });
  }
  if (dated.length < minDated) return [];
  dated.sort((a, b) => a.date.localeCompare(b.date));
  return limit > 0 ? dated.slice(0, limit) : dated;
}

// ── 26. macroShare(rows, { protein, carbs, fat, as }) ──────────────────────
// ROW ANNOTATION — macros as their SHARE OF THE ENERGY, which is the only
// reading that compares them.
//
// Grams do not: 92 g of protein and 82 g of fat are not the same amount of
// anything, and three bars drawn from grams say the meal was mostly carbs when
// it was mostly fat. The conversion (4/4/9 kcal per gram) is arithmetic, and
// the bind grammar deliberately has none — so it lives here, once, and both
// runtimes get the same percentages.
//
// A row with no macros at all annotates an EMPTY list, so the section gates
// itself off through `when` rather than drawing three zero bars.
const MACRO_KCAL = { protein: 4, carbs: 4, fat: 9 };
const MACRO_LABEL = { protein: 'Protein', carbs: 'Carbs', fat: 'Fat' };

function macroShare(rows, args = {}, feed, report) {
  const as = args.as || 'macro_share';
  const fields = {
    protein: args.protein || 'protein_g',
    carbs: args.carbs || 'carbs_g',
    fat: args.fat || 'fat_g',
  };
  const labels = { ...MACRO_LABEL, ...(args.labels || {}) };

  return rows.map((r) => {
    const parts = [];
    let total = 0;
    for (const key of ['protein', 'carbs', 'fat']) {
      const raw = getPath(r, fields[key]);
      if (!present(raw)) continue;
      const grams = Math.round(num(raw));
      const kcal = Math.round(grams * MACRO_KCAL[key]);
      total += kcal;
      parts.push({ key, label: labels[key], grams, kcal });
    }
    const list = parts.map((p) => {
      const share = total > 0 ? p.kcal / total : 0;
      const percent = Math.round(share * 100);
      return {
        ...p,
        share,
        // `value` is what a bar/Gauge binds without knowing what a macro is.
        value: percent,
        percent,
        percent_label: `${percent}%`,
        grams_label: `${formatNumber(p.grams)} g`,
        kcal_label: `${formatNumber(p.kcal)} cal`,
      };
    });
    return annotate(r, {
      [as]: list,
      [`${as}_kcal`]: total,
      [`${as}_kcal_label`]: total ? formatNumber(total) : '',
    }, report);
  });
}

// ── 27. childFacets(rows, { child, by, keys, order, stale_against, … }) ────
// ROW ANNOTATION — a nested ARRAY read as FACETS OF ONE FIELD. `nonEmptyPath`
// answers "which keys of this sub-OBJECT carry content"; this answers the same
// question of a sub-LIST that grows over time, where the key lives INSIDE each
// child (`{format:"word"}`) and the same key legitimately appears many times.
//
// The list-of-versions shape is what the bind grammar cannot reach: a page
// wants ONE pane per key holding THAT key's children newest-first, a badge row
// of the keys that exist, and a per-key "the source moved on since this was
// generated" flag — all three are a GROUP-BY plus a MAX plus a DATE COMPARE,
// and the grammar has neither grouping nor arithmetic.
//
//   <as>_keys    the declared `keys` PRESENT here, in DECLARED order (badges,
//                and which panes have content)
//   <as>_map     {key: bool} over every declared key (`when` gates: an absent
//                key is the EMPTY pane, which is a different page from a stale one)
//   <as>_by      {key: [its children, newest first]} — the pane binds one entry
//   <as>_latest  {key: newest child | null}
//   <as>_stale   {key: bool} — `row[stale_against]` is LATER than that key's
//                newest child, i.e. the source moved on and this output is
//                behind it. A key with NO children is `false`: it is empty, and
//                offering "Regenerate" for something never generated is a lie.
//   <as>_active  ONE key — the preselect ladder resolved: `prefer` when it names
//                a present key, else the first present key, else the first
//                declared one. It is a LADDER and not a bind fallback because
//                the page needs an answer BEFORE any tab is chosen.
//
// `prefer` arrives ALREADY RESOLVED (`context.js` `resolveArg`), so a context
// path like `page_args.tab` is its VALUE here — null on first paint, which the
// ladder then steps past. Ordering is a LEXICAL compare of the `order` field:
// ISO-8601 UTC sorts correctly as text, and text is what a payload carries.
// Ties keep feed order (the comparator falls back to the original index) so
// both runtimes emit the same list from the same rows.
//
// It does NOT touch `row[child]` and emits no url of its own — the children in
// `<as>_by` carry their own handles, and what a page does with those (hand to
// `host.download`) is the blueprint's business, not this derive's.
function childFacets(rows, args = {}, feed, report) {
  const child = args.child || 'children';
  const by = args.by || 'kind';
  const order = args.order || 'created_at';
  const as = args.as || 'facet';
  const declared = (Array.isArray(args.keys) ? args.keys : []).map((k) => String(k));
  const staleAgainst = args.stale_against || null;
  const prefer = present(args.prefer) ? String(args.prefer) : '';

  return rows.map((r) => {
    const raw = getPath(r, child);
    const list = Array.isArray(raw) ? raw : [];
    // Declared keys decide the buckets; with none declared they GROW from the
    // children in first-seen order, the way `nonEmptyPath` falls back to the
    // sub-object's own keys.
    const keys = [...declared];
    const buckets = new Map(keys.map((k) => [k, []]));
    list.forEach((c, i) => {
      if (!c || typeof c !== 'object') return;
      const v = getPath(c, by);
      if (!present(v)) return;
      const key = String(v);
      if (!buckets.has(key)) {
        if (declared.length) return;
        keys.push(key);
        buckets.set(key, []);
      }
      buckets.get(key).push({ c, i });
    });

    const at = (c) => String(getPath(c, order) ?? '');
    const byKey = {};
    const latest = {};
    const map = {};
    const stale = {};
    const presentKeys = [];
    const against = staleAgainst ? String(getPath(r, staleAgainst) ?? '') : '';
    for (const k of keys) {
      const sorted = [...buckets.get(k)].sort((a, b) => {
        const av = at(a.c);
        const bv = at(b.c);
        if (av !== bv) return av < bv ? 1 : -1;
        return a.i - b.i;
      }).map((x) => x.c);
      byKey[k] = sorted;
      latest[k] = sorted.length ? sorted[0] : null;
      map[k] = sorted.length > 0;
      if (sorted.length) presentKeys.push(k);
      stale[k] = Boolean(sorted.length && against && against > at(sorted[0]));
    }

    const active = presentKeys.includes(prefer) ? prefer : (presentKeys[0] ?? keys[0] ?? '');
    return annotate(r, {
      [`${as}_keys`]: presentKeys,
      [`${as}_map`]: map,
      [`${as}_by`]: byKey,
      [`${as}_latest`]: latest,
      [`${as}_stale`]: stale,
      [`${as}_active`]: active,
    }, report);
  });
}

// ── 28. checklistDay(plans, { checks, date }) ──────────────────────────────
// A CHECK LIST for one day: every folded plan row becomes one thing to do, and
// its `done` is read from the newest `check` row for (key, day). The plan is the
// schedule (what, when), the checks are the facts (done or not), and the two
// never live in one row — so a schedule edit cannot erase a tick and a tick
// cannot rewrite the schedule. Feeds a `CheckList` directly: {key, title, time,
// meta, done}. `time` is a display label ("8:00 AM") derived from the plan's
// `time` ("HH:mm"); `time_raw` keeps the sortable form. Order is by time, then
// title — the order a day is lived in, not the order rows were written.
function timeLabel(raw) {
  const m = /^(\d{1,2}):(\d{2})/.exec(String(raw || ''));
  if (!m) return '';
  const h = Number(m[1]);
  const mm = m[2];
  if (!Number.isFinite(h) || h < 0 || h > 23) return '';
  const hour12 = h % 12 === 0 ? 12 : h % 12;
  return `${hour12}:${mm} ${h < 12 ? 'AM' : 'PM'}`;
}

/** `8:00` / `08:00` / `21:00:00` → sortable `HH:mm`; anything else → ''. */
function timeRaw(v) {
  const m = /^(\d{1,2}):(\d{2})/.exec(String(v || ''));
  return m ? `${m[1].padStart(2, '0')}:${m[2]}` : '';
}

// ── plan `days` — WHICH DAYS a plan row is scheduled on ──────────────────────
// A medication is every day; a chore is "Mon and Thu" or "weekdays". The plan
// row says so in `days`: a list or a comma/space-separated string of weekday
// names (`mon,thu`, `Monday Thursday`), ISO numbers (1 = Mon … 7 = Sun), or the
// words `daily` / `everyday`, `weekdays`, `weekends`. Absent, empty or `daily`
// means EVERY day — so a plan row that never heard of `days` (every medication
// row ever written) schedules exactly as before. An unparseable value also
// means every day: a typo must not silently drop a chore from the week.
const WEEKDAY_TOKENS = { mon: 1, tue: 2, wed: 3, thu: 4, fri: 5, sat: 6, sun: 7 };
const WEEKDAY_SHORT_ISO = ['', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

/** `days` → sorted ISO weekday numbers, or null for every day. */
export function parseDays(v) {
  const tokens = Array.isArray(v) ? v.map((t) => String(t)) : String(v == null ? '' : v).split(/[\s,;/、，]+/);
  const out = new Set();
  for (const raw of tokens) {
    const t = raw.trim().toLowerCase();
    if (!t) continue;
    if (t === 'daily' || t === 'everyday' || t === 'every' || t === 'day' || t === '*' || t === 'all') return null;
    if (t === 'weekdays' || t === 'weekday') { [1, 2, 3, 4, 5].forEach((d) => out.add(d)); continue; }
    if (t === 'weekends' || t === 'weekend') { out.add(6); out.add(7); continue; }
    if (/^[1-7]$/.test(t)) { out.add(Number(t)); continue; }
    const n = WEEKDAY_TOKENS[t.slice(0, 3)];
    if (n) out.add(n);
  }
  return out.size === 0 || out.size === 7 ? null : [...out].sort((a, b) => a - b);
}

/** `[1,4]` → "Mon, Thu"; weekdays / weekends read as words; every day reads ''. */
export function daysLabel(days) {
  if (!days) return '';
  const s = days.join(',');
  if (s === '1,2,3,4,5') return 'Weekdays';
  if (s === '6,7') return 'Weekends';
  return days.map((d) => WEEKDAY_SHORT_ISO[d]).join(', ');
}

/** ISO weekday (1 = Mon … 7 = Sun) of a YYYY-MM-DD day. */
function isoWeekday(iso) {
  const d = new Date(`${iso}T12:00:00`).getDay();
  return d === 0 ? 7 : d;
}

function scheduledOn(plan, iso) {
  return !plan.days || plan.days.includes(isoWeekday(iso));
}

function planRows(rows) {
  return rows
    .filter((r) => r && present(r.title) && (present(r.key) || present(r.entry_id) || present(r.id)))
    .map((r) => {
      const days = parseDays(r.days);
      // the meta line is "who · note · which days": a medication row has only a note,
      // a chore row may name the person and the days — each part only when present.
      const parts = [
        present(r.who) ? String(r.who).trim() : '',
        present(r.note) ? String(r.note).trim() : (present(r.meta) ? String(r.meta).trim() : ''),
        daysLabel(days),
      ].filter(Boolean);
      return {
        // the agent's stable key; a row logged without one is still a row — its id stands in
        key: String(present(r.key) ? r.key : (r.entry_id ?? r.id)),
        title: String(r.title).trim(),
        time_raw: timeRaw(r.time),
        meta: parts.join(' · '),
        who: present(r.who) ? String(r.who).trim() : '',
        days,
        created_at: r.created_at || '',
        stock: r.stock == null || r.stock === '' ? null : num(r.stock),
      };
    })
    .sort((a, b) => (a.time_raw < b.time_raw ? -1 : a.time_raw > b.time_raw ? 1 : a.title.localeCompare(b.title)));
}

/** newest check per (key, day) → Map<`${key}|${day}`, done>. */
function checkMap(checks) {
  const newest = new Map();
  for (const c of Array.isArray(checks) ? checks : []) {
    if (!c || c.key == null) continue;
    const day = dayOf(c);
    if (!day) continue;
    const id = `${String(c.key)}|${day}`;
    const at = String(c.created_at || '');
    const prev = newest.get(id);
    if (prev && prev.at > at) continue;
    newest.set(id, { at, done: c.done === true });
  }
  return newest;
}

function checklistDay(rows, args = {}) {
  const date = isDayString(args.date) ? args.date.slice(0, 10) : todayIso();
  const marks = checkMap(args.checks);
  return planRows(rows)
    .filter((p) => scheduledOn(p, date))
    .map((p) => ({
      key: p.key,
      title: p.title,
      time: timeLabel(p.time_raw),
      time_raw: p.time_raw,
      meta: p.meta,
      date,
      done: marks.get(`${p.key}|${date}`)?.done === true,
    }));
}

// ── 29. checklistSummary(plans, { checks, date, days }) ────────────────────
// The numbers a check-list page prints beside its rows and could not compute
// itself (the bind grammar has no arithmetic): how many are done today, the
// NEXT open item, the streak of complete days, the completion rate over the
// last 30 days, and a 7-day bar series. Every number comes from plans + checks;
// nothing here is typed in by an agent. A day is COMPLETE when every plan row
// has a `done:true` check for it. The streak counts back from today when today
// is complete, else from yesterday, and never before the schedule existed (the
// earliest plan row's write day). The rate divides done by scheduled over the
// window, from that same first day — a tracker set up on Tuesday is not 0% for
// Monday.
function checklistSummary(rows, args = {}) {
  const today = isDayString(args.date) ? args.date.slice(0, 10) : todayIso();
  const days = Math.max(1, Math.trunc(num(args.days) || 7));
  const plans = planRows(rows);
  const marks = checkMap(args.checks);
  // Everything is counted per day against the plans SCHEDULED that day (`days`):
  // a day with nothing scheduled is neither complete nor missed — it is skipped.
  const onDay = (iso) => plans.filter((p) => scheduledOn(p, iso));
  const doneOn = (iso) => onDay(iso).reduce((n, p) => n + (marks.get(`${p.key}|${iso}`)?.done === true ? 1 : 0), 0);
  const complete = (iso) => { const n = onDay(iso).length; return n > 0 && doneOn(iso) >= n; };
  const shift = (iso, delta) => isoDay(new Date(new Date(`${iso}T12:00:00`).getTime() + delta * DAY_MS));
  const todays = onDay(today);
  const count = todays.length;

  let since = '';
  for (const p of plans) {
    const d = p.created_at ? isoDay(p.created_at) : '';
    if (d && (!since || d < since)) since = d;
  }
  if (!since || since > today) since = today;

  const taken = doneOn(today);
  const next = todays.find((p) => marks.get(`${p.key}|${today}`)?.done !== true) || null;

  let streak = 0;
  if (plans.length > 0) {
    let day = complete(today) ? today : shift(today, -1);
    while (day >= since && streak < 3660) {
      const n = onDay(day).length;
      if (n > 0 && doneOn(day) < n) break;
      if (n > 0) streak += 1;
      day = shift(day, -1);
    }
  }

  let scheduled = 0;
  let done = 0;
  if (plans.length > 0) {
    let day = shift(today, -29);
    if (day < since) day = since;
    while (day <= today) {
      const n = onDay(day).length;
      scheduled += n;
      done += Math.min(n, doneOn(day));
      day = shift(day, 1);
    }
  }
  const rate = scheduled === 0 ? 0 : Math.round((done * 100) / scheduled);

  const bars = [];
  for (let i = days - 1; i >= 0; i--) {
    const iso = shift(today, -i);
    const dt = new Date(`${iso}T12:00:00`);
    bars.push({
      date: iso,
      label: i === 0 ? 'Today' : WEEKDAY_SHORT[(dt.getDay() + 6) % 7],
      value: Math.min(onDay(iso).length, doneOn(iso)),
      future: false,
      selected: i === 0,
    });
  }

  // REFILLS — a medication whose plan rows carry `stock` (units left) reads
  // "N left, about D days" where D = stock / doses per day of that title; it is
  // listed when D <= 7. The plan row's stock is the user's own count (the agent
  // writes it when told); the page never guesses one.
  const perDay = new Map();
  for (const p of plans) perDay.set(p.title, (perDay.get(p.title) || 0) + 1);
  const seenRefill = new Set();
  const refills = [];
  for (const p of plans) {
    if (p.stock == null || seenRefill.has(p.title)) continue;
    seenRefill.add(p.title);
    const daysLeft = Math.floor(p.stock / Math.max(1, perDay.get(p.title) || 1));
    if (daysLeft > 7) continue;
    refills.push({ key: p.key, title: p.title, left: p.stock, days: daysLeft,
      line: `${formatNumber(p.stock)} left, about ${daysLeft} ${daysLeft === 1 ? 'day' : 'days'}.` });
  }

  return {
    today,
    day_label: dayLabel(today),
    since,
    dose_count: count,
    taken,
    all_done: count > 0 && taken >= count,
    next: next ? { key: next.key, title: next.title, time: timeLabel(next.time_raw), meta: next.meta } : null,
    first_time: todays.length ? timeLabel(todays[0].time_raw) : (plans.length ? timeLabel(plans[0].time_raw) : ''),
    streak,
    rate,
    refills,
    tiles: [
      { key: 'today', label: 'Today', value: taken, target: count },
      { key: 'streak', label: 'Day streak', value: streak },
      { key: 'rate', label: 'Last 30 days', value: rate, unit: '%' },
    ],
    bars,
  };
}

const WEEKDAY_SHORT = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

export const DERIVES = {
  statusCounts,
  todayTotals,
  weekBars,
  mealGroups,
  safetyList,
  distinctPhotos,
  modelPhoto,
  foldByKey,
  expiryStatus,
  mealCard,
  closetStats,
  // the generic family (README §7.3)
  hasField,
  nonEmptyPath,
  facetCounts,
  multiKeyCounts,
  groupByRoot,
  latestChildStatus,
  childFacets,
  rankStatus,
  unionStream,
  firstPhoto,
  mediaState,
  dateBuckets,
  pickRepresentative,
  resolveVisuals,
  dueTimeline,
  macroShare,
  checklistDay,
  checklistSummary,
};

/** Names this build implements — the bootstrap browser prints this list. */
export const DERIVE_NAMES = Object.keys(DERIVES);

/**
 * Run a named derive. `feed` is the read-only `{entries, folded}` view of the
 * whole widget, for the rare derive that must see a row outside its own `of`
 * without opening a selector cycle.
 *
 * GRACEFUL DEGRADATION, not fail-closed: a name this build does not implement
 * (a blueprint written for a newer app) returns an EMPTY result and reports
 * itself through `onIssue`. The page then degrades exactly the way it does for
 * a section with no rows — `when` hides it, a grid shows its EmptyState —
 * instead of drawing a red placeholder over an otherwise working widget. A
 * derive that throws is treated identically: a bad argument must not take the
 * page down with it.
 *
 * The report is what keeps this honest: the studio's fail-closed panel lists
 * every derive a page could not honour, so "renders degraded" never quietly
 * becomes "renders wrong".
 *
 * A ROW ANNOTATION additionally reports an `as` COLLISION through the same
 * channel (see `annotate`), deduped per output key so a 200-row feed says it
 * once rather than two hundred times.
 */
export function runDerive(expr, rows, args, feed, onIssue) {
  const fn = DERIVES[expr];
  if (typeof fn !== 'function') {
    onIssue?.({ __unsupported: 'derive', name: expr });
    return [];
  }
  const said = new Set();
  const report = (key) => {
    if (said.has(key)) return;
    said.add(key);
    onIssue?.({
      __unsupported: 'derive output collision',
      name: expr,
      detail: `"${key}" is already a field on the row — rename this derive's \`as\``,
    });
  };
  try {
    return fn(Array.isArray(rows) ? rows : [], args || {}, feed || {}, report);
  } catch (err) {
    onIssue?.({ __unsupported: 'derive error', name: expr, detail: err?.message || String(err) });
    return [];
  }
}
